import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Image } from '@zillow/constellation';

import ClaimActions from 'app/shared/actions/ClaimActions';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';

import { RMWContext } from 'app/shared/providers/RMWContext';
import WebSocketMessageNameConstants from 'app/shared/constants/WebSocketMessageNameConstants';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import InfoList from 'app/shared/components/WizardListingClaim/InfoList';
import ProgressBar from 'app/shared/components/ProgressBar';
import HomeBrowser from 'app/shared/images/HomeBrowser.svg';
import Trophy from 'app/shared/images/Trophy.svg';

import { StyledFadeInText, StyledWizardActionFooter } from './StyledComponents';

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
};

const STEPS = {
    INIT: 'init',
    TRANSFERRING: 'transferring',
    TRANSFERRED: 'transferred',
};

const STEP_CONFIGS = {
    [STEPS.INIT]: {
        title: 'Last step, transfer!',
        subtitle: 'Now we will transfer your listings to Zillow Rental Manager. A few things to note...',
        nextLabel: 'Start',
        icon: <Image src={HomeBrowser} />,
        gaLabel: AnalyticsConstants.CLAIM_LISTINGS_LABEL.TRANSFERT_INTRO,
    },
    [STEPS.TRANSFERRING]: {
        title: 'Last step, transfer!',
        subtitle: <StyledFadeInText>Transferring, this will only take a second...</StyledFadeInText>,
        nextLabel: 'Next',
        icon: <Image src={HomeBrowser} />,
        gaLabel: AnalyticsConstants.CLAIM_LISTINGS_LABEL.TRANSFERT_PROCESSING,
    },
    [STEPS.TRANSFERRED]: {
        title: <StyledFadeInText fontType="h3">Listings are transferred!</StyledFadeInText>,
        subtitle: (
            <StyledFadeInText>
                Next, you can edit and publish individually with Zillow Rental Manager.
            </StyledFadeInText>
        ),
        nextLabel: 'Go to properties',
        icon: <Image src={Trophy} />,
        gaLabel: AnalyticsConstants.CLAIM_LISTINGS_LABEL.TRANSFERT_COMPLETE,
    },
};

/* eslint-disable max-len */
const infos = [
    'Transferring will give you more direct control over how your listing appears on Zillow.',
    'You will also gain access to features like free virtual tours via the Zillow 3D Home Tours app, online applications, and online lease signing',
];
/* eslint-enable max-len */

const WizConvertListingInfo = ({
    onGoToPrevStep,
}) => {
    const { paths } = useContext(RMWContext);
    const dispatch = useDispatch();
    const [hasError, setHasError] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    const [currentStep, setCurrentStep] = useState(STEPS.INIT);
    const [progressPercentage, setProgressPercentage] = useState(0);

    const handleWebSocketMessage = (message) => {
        let data;
        try {
            data = JSON.parse(message.data);
        } catch (error) {
            return;
        }
        if (data.name !== WebSocketMessageNameConstants.USER_FEED_TO_MANUAL_STATUS) {
            return;
        }

        const { payload } = data;
        const { count } = payload;

        const {
            failed,
            succeeded,
            total,
        } = count;
        const conversionPercentage = ((failed + succeeded) / total) * 100;
        setProgressPercentage(conversionPercentage);
    };


    useEffect(() => {
        if (currentStep === STEPS.TRANSFERRING) {
            const interval = setInterval(() => {
                if (progressPercentage < 100) {
                    window.rentalManagerApiWebSocket.addEventListener('message', (message) => {
                        handleWebSocketMessage(message);
                    });
                } else {
                    setIsTransferring(false);
                    setCurrentStep(STEPS.TRANSFERRED);
                }
            }, 1000);

            return () => {
                window.rentalManagerApiWebSocket.removeEventListener('message', handleWebSocketMessage);
                clearInterval(interval);
            };
        }
    }, [currentStep, progressPercentage]);

    const handleGoToNextStep = () => {
        const analyticsOptions = { label: STEP_CONFIGS[currentStep].gaLabel };
        if (currentStep === STEPS.TRANSFERRED) {
            analyticsOptions.action = 'ClicksGoToProperties';
        }
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
            analyticsOptions,
        ));
        if (currentStep === STEPS.INIT) {
            setIsTransferring(true);
            dispatch(ClaimActions.convertAllProperties())
                .then(() => setCurrentStep(STEPS.TRANSFERRING))
                .catch(() => {
                    setIsTransferring(false);
                    setHasError(true);
                });
        }
        if (currentStep === STEPS.TRANSFERRING) {
            setCurrentStep(STEPS.TRANSFERRED);
        }
        if (currentStep === STEPS.TRANSFERRED) {
            window.router.transitionTo(paths.propertiesPage);
        }
    };

    const handleGoToPrevStep = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_BACK,
            { label: STEP_CONFIGS[currentStep].gaLabel },
        ));
        onGoToPrevStep();
    };

    return (
        <WizardBodyTemplate
            title={STEP_CONFIGS[currentStep].title}
            subtitle={STEP_CONFIGS[currentStep].subtitle}
            hasError={hasError}
            icon={STEP_CONFIGS[currentStep].icon}
        >
            {currentStep !== STEPS.INIT && (
                <ProgressBar
                    marginTop="sm"
                    marginBottom="xs"
                    value={progressPercentage}
                />
            )}
            <InfoList infos={infos} />
            <StyledWizardActionFooter
                onBackClick={handleGoToPrevStep}
                onNextClick={handleGoToNextStep}
                nextLabel={STEP_CONFIGS[currentStep].nextLabel}
                isBackDisabled={false}
                isNextDisabled={isTransferring}
            />
        </WizardBodyTemplate>
    );
};

WizConvertListingInfo.propTypes = propTypes;

export default WizConvertListingInfo;
